import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { leadsPipelineStatsEndpoint } from "@/config/endpoints";
import { transientRetry } from "@/lib/queryClient";
import type { PipelinePickerCounts } from "@/components/layout/header/StageStatusPicker";

/**
 * Filter-aware Stage / Status / Sub-status counts for the header pipeline
 * pickers. Fires `/core/leads/pipeline-stats` with the SAME filter params the
 * leads list uses, so every option count "swings" with branch / country /
 * search / date / other CFs — exactly like the branch and country pickers.
 *
 * The server strips the pipeline facets themselves (stage/status/sub) so
 * picking a stage doesn't zero its siblings, and pins status/sub counts to the
 * currently selected stage/status (passed through in `filters`). Counts are
 * `undefined` until the first response so callers can hide numbers while loading.
 */
export function usePipelineLeadStats(
  filters: Record<string, any> = {},
  options?: { enabled?: boolean },
): PipelinePickerCounts | undefined {
  const query = useQuery<PipelinePickerCounts>({
    queryKey: ["pipeline-stats", filters],
    queryFn: async () => {
      const res = await axiosInstance.get(leadsPipelineStatsEndpoint, {
        params: filters,
      });
      return {
        total: Number(res.data?.total ?? 0),
        stageCounts: (res.data?.stageCounts ?? {}) as Record<string, number>,
        statusCounts: (res.data?.statusCounts ?? {}) as Record<string, number>,
        subStatusCounts: (res.data?.subStatusCounts ?? {}) as Record<
          string,
          number
        >,
      };
    },
    enabled: options?.enabled !== false,
    staleTime: 60 * 1000,
    ...transientRetry,
  });

  return query.data;
}
